/**
 * Canvas polygon overlay rendering a closed contour from projected world points.
 *
 * Used in the 2D oblique view to show the vessel wall cross-section contour
 * and in 3D for selected diameter contour visualization.
 */

import type { Vec3 } from '../../domain';
import type { IViewport } from '../../viewports';
import type { IOverlayObject } from '../types';

export interface ContourOverlayConfig {
    readonly id: string;
    readonly contourPoints: readonly Vec3[];
    readonly color?: string;
    readonly lineWidth?: number;
}

export class ContourOverlay implements IOverlayObject {
    readonly id: string;
    readonly zIndex = 100;
    readonly interactive = false;
    readonly clipByCamera = false;
    visible = true;

    private contourPoints: readonly Vec3[];
    private color: string;
    private lineWidth: number;

    constructor(config: ContourOverlayConfig) {
        this.id = config.id;
        this.contourPoints = config.contourPoints;
        this.color = config.color ?? 'rgba(102,204,255,1.0)';
        this.lineWidth = config.lineWidth ?? 1;
    }

    setContourPoints(points: readonly Vec3[]): void {
        this.contourPoints = points;
    }

    setColor(color: string): void {
        this.color = color;
    }

    hitTest(_canvasX: number, _canvasY: number): boolean {
        return false;
    }

    onCameraChanged(_viewport: IViewport): void {
        // Canvas positions are updated in render()
    }

    render(ctx: CanvasRenderingContext2D, viewport: IViewport): void {
        if (this.contourPoints.length < 3) return;

        const projected = this.contourPoints.map((p) => viewport.worldToCanvas(p));
        const anyVisible = projected.some((p) => p.visible);
        if (!anyVisible) return;

        ctx.beginPath();
        ctx.moveTo(projected[0].x, projected[0].y);
        for (let i = 1; i < projected.length; i++) {
            ctx.lineTo(projected[i].x, projected[i].y);
        }
        ctx.closePath();

        ctx.strokeStyle = this.color;
        ctx.lineWidth = this.lineWidth;
        ctx.stroke();
    }
}
